import { useLazyQuery, useQuery } from "@apollo/client";
import { Grid } from "@mui/material";
import { debounce } from "lodash";
import { Dropdown, InputItem, LoadingSpinner } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  filterCatalogs,
  listRewards,
  listRewardTypes,
} from "../../../../../../graphql/queries/filterCatalogs";
import { listRewardUnits } from "../../../../../../graphql/queries/listRewardUnits";
import "./AddCatalogRewards.scss";
import {
  createRewardsFilter,
  formatCatalogsForDisplay,
  toggleReward,
} from "./common";
import RewardCard from "./RewardCard";

export default function AddCatalogRewards({
  selectedCatalogs,
  updateSelectedCatalogs,
  isSingularMode = false,
  shouldSetDefaultValue = false,
}) {
  const [callListRewards, { data: rewardsData, loading }] =
    useLazyQuery(listRewards);
  const { data: catalogList } = useQuery(filterCatalogs, {
    variables: {
      filters: { status: ["APPROVED"], limit: 100, offset: 0, toAttach: true },
    },
  });
  const { data: rewardUnitsData } = useQuery(listRewardUnits, {
    variables: {
      size: 100,
    },
  });
  const { data: rewardTypesData } = useQuery(listRewardTypes, {
    variables: {
      page: 0,
      size: 100,
    },
  });
  const [searchText, setSearchText] = useState("");
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);

  const displayCatalogs = useMemo(
    () => formatCatalogsForDisplay(catalogList?.filterCatalogs?.items),
    [catalogList?.filterCatalogs?.items]
  );

  const rewardUnitAndTypeMaps = useMemo(() => {
    const unitsMap = {};
    rewardUnitsData?.listRewardUnits?.items?.forEach((unit) => {
      unitsMap[unit.id] = unit.unit;
    });

    const typesMap = {};
    rewardTypesData?.listRewardType?.items?.forEach((type) => {
      typesMap[type.id] = type.name;
    });

    return { unitsMap, typesMap };
  }, [
    rewardUnitsData?.listRewardUnits?.items,
    rewardTypesData?.listRewardType?.items,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callAfterChange = useCallback(
    debounce((searchText, catalogs = [], selectedIds = []) => {
      const filter = createRewardsFilter(searchText, selectedIds, catalogs);
      if (!filter) return;

      callListRewards({
        variables: { filter, page: 0, size: 1000 },
        fetchPolicy: "no-cache",
      });
    }, 500),
    []
  );

  useEffect(() => {
    callAfterChange(
      searchText,
      catalogList?.filterCatalogs?.items,
      selectedCatalogIds
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, catalogList?.filterCatalogs?.items, selectedCatalogIds]);

  const [currentSelectedRewards, setCurrentSelectedRewards] = useState([]);

  // Reset currentSelectedRewards when modal opens
  useEffect(() => {
    if (selectedCatalogs?.rewards?.length === 0) {
      setCurrentSelectedRewards([]);
    }
    if (shouldSetDefaultValue && selectedCatalogs?.rewards?.length > 0) {
      setCurrentSelectedRewards(selectedCatalogs?.rewards);
    }
  }, [selectedCatalogs?.rewards, shouldSetDefaultValue]);

  const onRewardSelect = (reward) => {
    let updatedRewards;

    if (isSingularMode) {
      // For singular mode, replace the current selection with the new reward
      const rewardId = reward.id || reward.reward_id;
      const currentReward = selectedCatalogs?.rewards?.[0];

      // If clicking the same reward, deselect it
      if (currentReward?.reward_id === rewardId) {
        updatedRewards = [];
      } else {
        // Replace with the new reward
        updatedRewards = [
          {
            reward_name: reward.name,
            total_quantity: reward.quantity,
            image_url: reward.imageUrl,
            reward_id: rewardId,
            catalog_id: reward.catalogId,
            reward_unit_id: reward.rewardUnitId,
            rank_range: {
              from: "",
              to: "",
            },
          },
        ];
      }
    } else {
      // For multiple mode, use the original toggle logic
      updatedRewards = toggleReward(reward, currentSelectedRewards);
    }

    // Update only the local state, not the parent component yet
    setCurrentSelectedRewards(updatedRewards);
    updateSelectedCatalogs({
      ...selectedCatalogs,
      rewards: updatedRewards,
    });
  };

  return (
    <div className="mt-2 reward-catalog-content">
      {isSingularMode && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "4px",
            padding: "8px 12px",
            marginBottom: "16px",
            color: "#856404",
            fontSize: "14px",
          }}
        >
          Only one reward can be selected at a time
        </div>
      )}

      <Grid container columnSpacing={2} className="mb-3">
        <Grid item xs={6}>
          <div className="display-flex align-items-center">
            <InputItem
              placeholder="Search reward name"
              trailingIcon="search"
              size="medium"
              style={{
                borderRadius: 0,
                border: "none",
                borderBottom: "1px solid #c0c0c0",
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />{" "}
            <div className="multiple-select-wrap peacock-dropdown-show-label ml-3">
              <Dropdown
                childList={displayCatalogs}
                data-testid="status-filter"
                placeholder="Reward Catalog"
                selection={isSingularMode ? "single" : "multiple"}
                selectedValue={selectedCatalogIds}
                setSelectedValue={(value) => setSelectedCatalogIds(value || [])}
              />
            </div>
          </div>
        </Grid>
      </Grid>

      <Grid container columnSpacing={2}>
        <Grid item xs={isSingularMode ? 12 : 9}>
          <Grid container spacing={1}>
            {loading && (
              <Grid item xs={12} data-testid="loading-spinner">
                <LoadingSpinner
                  size={{
                    height: "40px",
                    width: "40px",
                  }}
                />
              </Grid>
            )}
            {!loading && rewardsData?.listRewards?.items?.length === 0 && (
              <Grid item xs={12}>
                <h4 className="font-md">No rewards found</h4>
              </Grid>
            )}
            {!loading &&
              rewardsData?.listRewards?.items?.length > 0 &&
              rewardsData?.listRewards?.items?.map((reward) => (
                <Grid
                  item
                  xs={6}
                  md={3}
                  xl={3}
                  className="cursor-pointer"
                  key={`${reward.id}-${reward.name}`}
                  onClick={() => onRewardSelect(reward)}
                  data-testid={`reward-item-${reward.id}`}
                >
                  <RewardCard
                    reward={reward}
                    isSelected={
                      !!currentSelectedRewards.find(
                        (item) => item.reward_id === reward.id
                      )
                    }
                    onClick={() => onRewardSelect(reward)}
                    rewardUnitAndTypeMaps={rewardUnitAndTypeMaps}
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
